import { useState } from 'react';
import { useFormikContext } from 'formik';
import { Button, Stack, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/AddOutlined';
import { AppointmentType } from '@prisma/client';
import { addMinutes } from 'date-fns';
import {
  CreateAptValues,
  DEFAULT_APT_LENGTH,
} from '../../utils/form/appointmentForm';
import {
  getCalendarTimezone,
  getLengthOptions,
  getTakenSessionSlots,
} from '../../helpers/appointments';
import Calendar from '../Scheduler/Calendar';
import useUser from '../../hooks/global/useUser';
import useCalendarSelectedAppointment from '../../hooks/appointments/useCalendarSelectedAppointment';
import theme from '../../constants/theme';
import FormSmallModal, { FormData } from '../global/FormSmallModal';
import {
  durationFormSchema,
  DurationFormState,
  TakenSessionSlot,
} from '../../types/appointments';

interface CalendarInputProps {
  appointmentLengthInMinutes: string | undefined;
  startDateTime: string;
  onChange: (value: Date) => void;
  onDelete: (() => void) | undefined;
  rescheduleAppointmentId?: string;
  error?: string;
  name?: string;
  takenSlots?: TakenSessionSlot[];
}

const getCalendarInputName = (index: number) =>
  `additionalAppointments.${index}.startDateTime`;

function CalendarInput({
  appointmentLengthInMinutes,
  startDateTime,
  onChange,
  onDelete,
  rescheduleAppointmentId,
  error,
  name,
  takenSlots,
}: CalendarInputProps) {
  const { user: loadedUser } = useUser();
  const { values } = useFormikContext<CreateAptValues>();

  return (
    <div className="calendar-input">
      <Calendar
        name={name}
        employeeId={values.employeeId}
        error={error}
        appointmentLengthInMinutes={
          appointmentLengthInMinutes || DEFAULT_APT_LENGTH
        }
        startDateTime={startDateTime}
        onChange={onChange}
        key={`${values.employeeId}-appointment-form`}
        disabled={false}
        rescheduleAppointmentId={rescheduleAppointmentId}
        serviceCategory={
          // 0 or null/undefined is for tattoo
          +(values.serviceId || 0) ? undefined : 'TATTOO'
        }
        timezone={getCalendarTimezone(
          loadedUser ?? undefined,
          values.businessTripId,
        )}
        onDelete={onDelete}
        takenSlots={takenSlots}
      />
    </div>
  );
}

function AppointmentDate() {
  const { selectedAppointment } = useCalendarSelectedAppointment();
  const { values, touched, setFieldValue, setFieldTouched, errors } =
    useFormikContext<CreateAptValues>();
  const { user } = useUser();

  const [isDurationModalOpen, setIsDurationModalOpen] = useState(false);

  const allowMultiSession =
    values.appointmentType === 'NEW_APPOINTMENT' && !selectedAppointment;

  const onChangeMainSession = (value: Date) => {
    setFieldValue('startDateTime', value);
  };

  const onChangeSession = (index: number, value: Date) => {
    const additionalAppointments = values.additionalAppointments || [];
    const endDateTime = addMinutes(
      new Date(value),
      parseInt(additionalAppointments[index].appointmentLengthInMinutes, 10),
    );
    additionalAppointments[index] = {
      ...additionalAppointments[index],
      startDateTime: value.toISOString(),
      endDateTime: endDateTime.toISOString(),
    };
    setFieldValue('additionalAppointments', additionalAppointments);
  };

  const onAddSession = (appointmentLengthInMinutes: string) => {
    const additionalAppointments = values.additionalAppointments || [];
    additionalAppointments.push({
      appointmentLengthInMinutes:
        appointmentLengthInMinutes || DEFAULT_APT_LENGTH,
      startDateTime: '',
      endDateTime: '',
    });
    setFieldValue('additionalAppointments', additionalAppointments);
  };

  const onDeleteSession = (index: number) => {
    if (index < 0) {
      // move all appointments up and remove the first one, setting the first one to the original appointment
      const additionalAppointmentsClone = [
        ...(values.additionalAppointments || []),
      ];
      const mainSession = additionalAppointmentsClone.shift();
      if (!mainSession) return;

      setFieldValue('startDateTime', mainSession.startDateTime);
      setFieldValue('endDateTime', mainSession.endDateTime);
      setFieldValue(
        'appointmentLengthInMinutes',
        mainSession.appointmentLengthInMinutes,
      );
      setFieldValue('additionalAppointments', additionalAppointmentsClone);
    } else {
      const additionalAppointments = values.additionalAppointments || [];
      additionalAppointments.splice(index, 1);
      setFieldValue('additionalAppointments', additionalAppointments);
      // @ts-expect-error Wrong type
      if (touched.additionalAppointments?.[index]?.startDateTime) {
        setFieldTouched(getCalendarInputName(index), false);
      }
      // if next appointment was the last one and moved down an index, set the touched to false
      const nextAppointmentIndex = index + 1;
      if (
        // @ts-expect-error Wrong type
        touched.additionalAppointments?.[nextAppointmentIndex]?.startDateTime &&
        !additionalAppointments[nextAppointmentIndex]
      ) {
        setFieldTouched(getCalendarInputName(nextAppointmentIndex), false);
      }
    }
  };

  const appointmentLengthOptions = getLengthOptions({
    user,
    appointmentType: values.appointmentType as AppointmentType,
    serviceId: values.serviceId ? Number(values.serviceId) : undefined,
  });

  const durationForm: FormData<DurationFormState> = {
    title: 'Add another appointment',
    initialValues: {
      appointmentLengthInMinutes: parseInt(
        values.appointmentLengthInMinutes || DEFAULT_APT_LENGTH,
        10,
      ),
    },
    inputRows: [
      [
        {
          inputType: 'select',
          label: 'Appointment length',
          tooltip: 'How long do you want to schedule the new appointment for?',
          name: 'appointmentLengthInMinutes',
          options: (appointmentLengthOptions || [])?.map(option => ({
            value: Number(option.value) as unknown as string,
            label: String(option.label),
          })),
        },
      ],
    ],
    onSubmit: ({ appointmentLengthInMinutes }) => {
      onAddSession(String(appointmentLengthInMinutes));
      setIsDurationModalOpen(false);
    },
    submitButtonText: 'Add',
    validationSchema: durationFormSchema,
  };

  if (values.appointmentType === 'WALK_IN') return null;

  return (
    <>
      <Stack gap={1} alignItems="flex-start">
        <div className="input-group no-gap">
          <Typography variant="h2" mb={2.5}>
            Select date & time
          </Typography>
          <CalendarInput
            error={errors?.startDateTime}
            appointmentLengthInMinutes={values.appointmentLengthInMinutes}
            startDateTime={values.startDateTime}
            rescheduleAppointmentId={selectedAppointment?.appointmentId}
            onChange={onChangeMainSession}
            onDelete={
              values?.additionalAppointments?.length
                ? () => onDeleteSession(-1)
                : undefined
            }
            takenSlots={getTakenSessionSlots(-1, values)}
          />
          {values.additionalAppointments?.map((session, index) => (
            <CalendarInput
              name={getCalendarInputName(index)}
              // @ts-expect-error Wrong type
              error={errors?.[getCalendarInputName(index)]}
              // eslint-disable-next-line react/no-array-index-key
              key={`${values.employeeId}-appointment-form-${index}`}
              appointmentLengthInMinutes={session.appointmentLengthInMinutes}
              startDateTime={session.startDateTime}
              onChange={value => onChangeSession(index, value)}
              onDelete={() => onDeleteSession(index)}
              takenSlots={getTakenSessionSlots(index, values)}
            />
          ))}
        </div>
        {allowMultiSession && (
          <Button
            variant="link"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              fontSize: theme.typography.body2.fontSize,
            }}
            onClick={() => setIsDurationModalOpen(true)}>
            <AddIcon fontSize="inherit" />
            <Typography variant="body2">Add another appointment</Typography>
          </Button>
        )}
      </Stack>
      {isDurationModalOpen && (
        <FormSmallModal
          open
          onClose={() => setIsDurationModalOpen(false)}
          form={durationForm}
          allowUntouched
        />
      )}
    </>
  );
}

export default AppointmentDate;
